import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Card from 'components/card';
import Nodatafound from 'pages/Common/Nodatafound';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import { ThreeDots } from 'react-loader-spinner';
import toast from 'react-hot-toast';
import { MdClose, MdGroupAdd, MdSearch, MdStopCircle } from 'react-icons/md';

import studentAssignmentServices from 'ApiService/Placement/studentAssignmentServices';
import { AuthContext } from 'contexts/AuthContext';
import StudentListView from './components/StudentListView';

const SELECT_CLASS =
  'w-full rounded-xl border border-gray-200 bg-lightPrimary py-2.5 pl-3 pr-3 text-sm outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white';

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

/**
 * Which ownership scopes this user may pick between. Mirrors resolveScope() on
 * the server — the server is the enforcement point, this only decides what to
 * render.
 */
const scopeOptionsFor = (user) => {
  const role = user?.role;
  const hrRole = user?.hrRole;
  const isAdmin = role === 'admin' || role === 'superadmin';
  const isHeadHR = role === 'headhr' || hrRole === 'headhr';
  const isJrHRWithPermission =
    (role === 'jrhr' || hrRole === 'jrhr') && user?.allPermission;

  if (isAdmin) {
    return [
      { value: 'personal', label: 'Personal' },
      { value: 'other', label: 'Other' },
      { value: 'all', label: 'All' },
    ];
  }
  if (isHeadHR || isJrHRWithPermission) {
    return [
      { value: 'personal', label: 'Personal' },
      { value: 'other', label: 'Other' },
    ];
  }
  // Plain HR and JrHR without allPermission see only their own data.
  return [];
};

// Admins open on the widest view; Head HR opens on their own drives.
const defaultScopeFor = (user) =>
  user?.role === 'admin' || user?.role === 'superadmin' ? 'all' : 'personal';

const Index = () => {
  const { state } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const scopeOptions = useMemo(
    () => scopeOptionsFor(state?.user),
    [state?.user]
  );
  const canChooseScope = scopeOptions.length > 0;

  const [scope, setScope] = useState(() => defaultScopeFor(state?.user));
  const [hrId, setHrId] = useState('');
  const [search, setSearch] = useState('');
  const [activeDepartment, setActiveDepartment] = useState('all');

  const [cards, setCards] = useState([]);
  const [hrs, setHrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);

  const [endTarget, setEndTarget] = useState(null);
  const [ending, setEnding] = useState(false);

  // Same component is mounted under /admin, /superadmin, /placement and
  // /headhr, so derive the prefix instead of hardcoding one.
  const layoutPrefix = `/${location.pathname.split('/')[1]}`;
  const showHrColumn = canChooseScope && scope !== 'personal';

  const fetchCards = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (canChooseScope) params.scope = scope;
      if (hrId) params.hrId = hrId;

      const response = await studentAssignmentServices.getOngoingInterviews(
        params
      );
      if (response.data.success) {
        setCards(response.data.cards || []);
        setHrs(response.data.hrs || []);
      } else {
        toast.error('Failed to fetch ongoing interviews');
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch ongoing interviews'
      );
    } finally {
      setLoading(false);
    }
  }, [canChooseScope, scope, hrId]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const departments = useMemo(() => {
    const seen = new Map();
    cards.forEach((card) => {
      if (card.departmentId && !seen.has(String(card.departmentId))) {
        seen.set(String(card.departmentId), card.departmentName);
      }
    });
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [cards]);

  const visibleCards = useMemo(() => {
    const term = search.trim().toLowerCase();
    return cards.filter((c) => {
      const matchesDept =
        activeDepartment === 'all' ||
        String(c.departmentId) === activeDepartment;
      const matchesSearch =
        !term || (c.companyName || '').toLowerCase().includes(term);
      return matchesDept && matchesSearch;
    });
  }, [cards, activeDepartment, search]);

  const handleScopeChange = (value) => {
    setScope(value);
    setHrId('');
    setSearch('');
    setActiveDepartment('all');
  };

  const handleAssignStudent = (card) => {
    navigate(`${layoutPrefix}/hr/assign-to-company`, {
      state: {
        prefill: {
          companyId: card.companyId,
          departmentId: card.departmentId,
          roleId: card.roleId,
        },
      },
    });
  };

  const handleEndProcess = async () => {
    if (!endTarget) return;
    setEnding(true);
    try {
      const response = await studentAssignmentServices.endInterviewProcess({
        companyId: endTarget.companyId,
        departmentId: endTarget.departmentId,
        roleId: endTarget.roleId || '',
        hrId: endTarget.hrId,
      });
      if (response.data.success) {
        toast.success('Interview process ended');
        setEndTarget(null);
        fetchCards();
      } else {
        toast.error(response.data.message || 'Failed to end process');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to end process');
    } finally {
      setEnding(false);
    }
  };

  if (selectedCard) {
    return (
      <StudentListView
        card={selectedCard}
        hrs={showHrColumn ? hrs : []}
        onBack={() => setSelectedCard(null)}
      />
    );
  }

  return (
    // No page heading here: the layout navbar already renders the route name
    // ("Ongoing Interviews") directly above this view.
    <div className="mt-4 h-full w-full">
      {canChooseScope && (
        <div className="mb-4 inline-flex overflow-hidden rounded-xl border border-gray-200 dark:border-white/10">
          {scopeOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleScopeChange(option.value)}
              className={`px-5 py-2 text-sm font-medium transition-colors ${
                scope === option.value
                  ? 'bg-brand-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-navy-800 dark:text-gray-300 dark:hover:bg-navy-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-end gap-4">
        <div className="w-full max-w-xs">
          <label className="mb-1 block text-sm text-gray-600 dark:text-white">
            Search Company
          </label>
          <div className="relative">
            <MdSearch className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Company name"
              className={`${SELECT_CLASS} !pl-10 !pr-9`}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <MdClose className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {showHrColumn && (
          <div className="w-full max-w-xs">
            <label className="mb-1 block text-sm text-gray-600 dark:text-white">
              HR
            </label>
            <select
              value={hrId}
              onChange={(e) => setHrId(e.target.value)}
              className={SELECT_CLASS}
            >
              <option value="">All HRs</option>
              {hrs.map((hr) => (
                <option key={hr._id} value={hr._id}>
                  {hr.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {departments.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-6 border-b border-gray-200 dark:border-white/10">
          <button
            onClick={() => setActiveDepartment('all')}
            className={`pb-3 text-sm font-medium transition-colors ${
              activeDepartment === 'all'
                ? 'border-b-2 border-brand-500 text-brand-500'
                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            All
          </button>
          {departments.map((dept) => (
            <button
              key={dept.id}
              onClick={() => setActiveDepartment(dept.id)}
              className={`pb-3 text-sm font-medium transition-colors ${
                activeDepartment === dept.id
                  ? 'border-b-2 border-brand-500 text-brand-500'
                  : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {dept.name}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <ThreeDots color="#4318FF" height={60} width={60} />
        </div>
      ) : visibleCards.length === 0 ? (
        <Nodatafound
          title={
            search.trim()
              ? `No company matching "${search.trim()}"`
              : 'No ongoing interviews'
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {visibleCards.map((card) => (
            <Card
              key={`${card.companyId}-${card.departmentId}-${card.roleId}-${card.hrId}`}
              extra="p-5 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setSelectedCard(card)}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-base font-bold text-navy-700 dark:text-white">
                  {card.companyName}
                </p>
                <span className="whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                  {formatDate(card.startDate)}
                </span>
              </div>

              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                {card.departmentName}
              </p>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {card.roleName || 'JD Attachment'}
              </p>

              {showHrColumn && card.hrName && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  by: {card.hrName}
                </p>
              )}

              <div className="mt-4 space-y-2">
                {[
                  ['total assigned', card.assignedCount],
                  ['accepted student', card.acceptedCount],
                  ['declined student', card.declinedCount],
                  ['auto declined student', card.autoDeclinedCount],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 dark:border-white/10"
                  >
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {label}
                    </span>
                    <span className="text-sm font-bold text-navy-700 dark:text-white">
                      {value ?? 0}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAssignStudent(card);
                  }}
                  className="flex items-center gap-1 rounded-xl bg-brand-500 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-brand-600"
                >
                  <MdGroupAdd className="h-4 w-4" />
                  Assign Student
                </button>
                {card.canEndProcess && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEndTarget(card);
                    }}
                    className="flex items-center gap-1 rounded-xl border border-red-500 px-3 py-2 text-xs font-medium text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-navy-700"
                  >
                    <MdStopCircle className="h-4 w-4" />
                    End Process
                  </button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <ConfirmationModal
        isOpen={!!endTarget}
        onClose={() => setEndTarget(null)}
        onConfirm={handleEndProcess}
        disableConfirm={ending}
        confirmText="End Process"
        icon="warning"
        title="End interview process?"
        message={
          endTarget
            ? `This removes "${endTarget.companyName} — ${
                endTarget.roleName || 'JD Attachment'
              }" from the Ongoing Interviews tab. Student statuses and Assignment Overview are not changed, and the card returns if you assign a new student later.`
            : ''
        }
      />
    </div>
  );
};

export default Index;
